'use client';

import { useEffect, useRef, useContext } from 'react';
import { API_METHODS } from '@/modules/api/common';
import { StatusEnum } from '@/types';
import AttachmentsToolbarContext from '../AttachmentsToolbar.context';
import { updateAttachmentAction, updateAttachmentStatusAction, updateAttachmentProgressAction } from '../AttachmentsToolbar.module';
import { updateUploadStatus, requestUploadUrl } from '../AttachmentToolbar.action';
import type { AttachmentUploadContext, AttachmentUploadState } from '@/components/AttachmentsToolbar';
const updateAttachmentStatus = async (dispatch: AttachmentUploadContext['dispatch'], {
  attachment_id,
  status,
  opts
}: Pick<AttachmentUploadState, 'attachment_id'> & {
  status: StatusEnum.DONE | StatusEnum.FAILED;
} & {
  opts?: Record<string, any>;
}) => {
  const updateReq = await updateUploadStatus({
    attachment_id,
    status,
    opts
  });
  dispatch(updateAttachmentStatusAction({
    attachment_id,
    status: updateReq?.isSuccess ? status : StatusEnum.FAILED
  }));
};
const registerUploadListeners = (dispatch: AttachmentUploadContext['dispatch'], xhr: XMLHttpRequest, abortController: React.MutableRefObject<(() => void) | null>) => ({
  attachment_id,
  pre_signed_url,
  opts
}: Pick<AttachmentUploadState, 'attachment_id' | 'pre_signed_url'> & {
  opts?: Record<string, any>;
}) => {
  xhr.addEventListener('loadstart', () => {
    dispatch(updateAttachmentAction({
      attachment_id,
      status: StatusEnum.PENDING,
      pre_signed_url
    }));
  });
  xhr.addEventListener('load', async () => {
    await updateAttachmentStatus(dispatch, {
      attachment_id,
      status: StatusEnum.DONE,
      opts
    });
  });
  xhr.addEventListener('loadend', () => {
    abortController.current = null;
  });
  xhr.upload.addEventListener('progress', event => {
    if (event.lengthComputable) {
      dispatch(updateAttachmentProgressAction({
        attachment_id,
        progress: Number((event.loaded / event.total * 100).toFixed(0))
      }));
    }
  });
  xhr.addEventListener('error', async () => {
    await updateAttachmentStatus(dispatch, {
      attachment_id,
      status: StatusEnum.FAILED
    });
  });
  xhr.addEventListener('abort', async () => {
    await updateAttachmentStatus(dispatch, {
      attachment_id,
      status: StatusEnum.FAILED
    });
  });
};
const uploadAttachment = async (dispatch: AttachmentUploadContext['dispatch'], abortController: React.MutableRefObject<(() => void) | null>, attachment: AttachmentUploadState) => {
  if (abortController.current !== null) {
    return;
  }
  abortController.current = () => {};
  const requestOpts = attachment?.tenant ? {
    headers: {
      'x-tenant-name': attachment.tenant
    }
  } : {};
  const statusUpdateOpts = {
    ...requestOpts,
    invalidateTags: attachment?.invalidateTags
  };
  const uploadUrlReq = await requestUploadUrl({
    attachment_id: attachment?.attachment_id,
    name: attachment.name,
    entity_type: attachment.entity_type,
    entity_id: attachment?.entity_id,
    opts: requestOpts
  });
  if (uploadUrlReq?.isSuccess) {
    const xhr = new XMLHttpRequest();
    xhr.open(API_METHODS.PUT, uploadUrlReq.response.pre_signed_url!);
    registerUploadListeners(dispatch, xhr, abortController)({
      attachment_id: attachment.attachment_id,
      pre_signed_url: uploadUrlReq.response.pre_signed_url,
      opts: statusUpdateOpts
    });
    xhr.send(attachment.file);
    abortController.current = () => xhr?.abort();
  } else {
    await updateAttachmentStatus(dispatch, {
      attachment_id: attachment.attachment_id,
      status: StatusEnum.FAILED
    });
    abortController.current = null;
  }
};
const useUploadPendingAttachment = (attachment_id: AttachmentUploadState['attachment_id']) => {
  const {
    dispatch,
    attachments
  } = useContext(AttachmentsToolbarContext);
  const abortController = useRef<(() => void) | null>(null);
  const attachment = attachments.find(/* AUTO ADDED DELETE ME */
  /* eslint-disable-next-line @typescript-eslint/no-shadow */
  attachment => attachment.attachment_id === attachment_id);
  const initiateUpload = attachment && abortController?.current === null && !attachment?.pre_signed_url && attachment?.status === StatusEnum.PENDING;
  useEffect(() => {
    const asyncUploadAttachment = async () => {
      await uploadAttachment(dispatch, abortController, attachment!);
    };
    if (initiateUpload) {
      asyncUploadAttachment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiateUpload]);
  return abortController.current;
};
export default useUploadPendingAttachment;